import { PlotSeed, Seed, PlotSeedOut, PlotDictionarySeed, WarehouseSeed } from '../models/Seed';
import { PagingParams, PagedResults } from '@abaco/web-common/src/Paging';
import { HTTPClient, propsToDate, propsToDatePaged } from '@abaco/web-common/src/HTTP';
import { ColtivationSystem } from '../models/Plot';

export default class VarietyApi {

    private http: HTTPClient;

    constructor(http: HTTPClient) {
        this.http = http;
    }

    async getVegCycles(plot_id: number, paging: PagingParams, block_id?: number): Promise<PagedResults<PlotSeed>> {
        let res;
        if (block_id) {
            res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/blocks/${block_id}/vegetative_cycles`, {
                params: {
                    offset: paging.offset,
                    count: paging.count
                }
            });
        } else {
            res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/vegetative_cycles`, {
                params: {
                    offset: paging.offset,
                    count: paging.count
                }
            });
        }
        propsToDatePaged(res.data, ['start_date', 'end_date'])
        return res.data;
    }

    //used for block with planting so only one and only veg cycle
    async getBlockVariety(plot_id: number, block_id: number): Promise<PlotSeed | null> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/blocks/${block_id}/vegetative_cycles`, {
            params: {
                offset: 0,
                count: 1
            }
        });
        propsToDatePaged(res.data, ['start_date', 'end_date'])
        return res.data.records.length > 0 ? res.data.records[0] : null;
    }

    async getAllSeeds(pk_cuaa: number, campaign: number, crop_code: string, filter: string | null, paging: PagingParams): Promise<PagedResults<Seed>> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/land_uses/${pk_cuaa}/${campaign}/crops/${crop_code}/dictionary_seeds`, {
            params: {
                search: filter?.replace("[", "").replace("]", "").toLowerCase(),
                sort: "+seed_name",
                offset: paging.offset,
                count: paging.count
            }
        })

        return res.data;
    }

    async getAllClonesOfSeed(pk_cuaa: number, campaign: number, crop_code: string, item_id: number, search: string | null, paging: PagingParams): Promise<PagedResults<Seed>> {
        const filter = [];
        filter.push(`clone_of eq ${item_id}`);
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/land_uses/${pk_cuaa}/${campaign}/crops/${crop_code}/dictionary_seeds`, {
            params: {
                search: search?.replace("[", "").replace("]", "").toLowerCase(),
                sort: "+seed_name",
                offset: paging.offset,
                count: paging.count,
                filter: filter
            }
        })

        return res.data;
    }

    async getSeeds(plot_id: number, hasClones: boolean, search: string | null, paging: PagingParams): Promise<PagedResults<PlotDictionarySeed>> {
        const filter = [];
        if (!hasClones) {
            filter.push(`clone_of eq`);
        }
        if (search) {
            filter.push(`^full_text_search like ${search.replace("[", "").replace("]", "").toUpperCase()}`);
        }
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/dictionary_seeds`, {
            params: {
                offset: paging.offset,
                count: paging.count,
                sort: "+seed_name",
                filter: filter
            }
        })

        return res.data;
    }

    async getSeed(plot_id: number, id: number): Promise<PlotDictionarySeed> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/dictionary_seeds`, {
            params: {
                offset: 0,
                count: 1,
                filter: "seed_id eq " + id
            }
        })
        return res.data.records[0];
    }


    async getClonesOfSeed(plot_id: number, item_id: number, search: string | null, paging: PagingParams): Promise<PagedResults<PlotDictionarySeed>> {
        const filter = [];
        filter.push(`clone_of eq ${item_id}`);
        if (search) {
            filter.push(`^full_text_search like ${search.replace("[", "").replace("]", "").toUpperCase()}`);
        }
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/dictionary_seeds`, {
            params: {
                offset: paging.offset,
                count: paging.count,
                sort: "+seed_name",
                filter: filter
            }
        })

        return res.data;
    }

    async editVegCycle(plot_id: number, cycle_number: number, editedVegCycle: PlotSeedOut, block_id?: number): Promise<PlotSeed> {
        let res;
        if (block_id) {
            res = await this.http.put(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/blocks/${block_id}/vegetative_cycles/${cycle_number}`, editedVegCycle)
        } else {
            res = await this.http.put(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/vegetative_cycles/${cycle_number}`, editedVegCycle)
        }
        propsToDate(res.data, ['start_date', 'end_date'])
        return res.data;
    }

    async insertVegCycle(plot_id: number, plotSeedOut: PlotSeedOut, block_id?: number): Promise<PlotSeed> {
        let res;
        if (block_id) {
            res = await this.http.post(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/blocks/${block_id}/vegetative_cycles`, plotSeedOut)
        } else {
            res = await this.http.post(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/vegetative_cycles`, plotSeedOut)
        }
        propsToDate(res.data, ['start_date', 'end_date'])
        return res.data;
    }

    async deleteVariety(plot_id: number, cycle_number: number, block_id?: number): Promise<PlotSeed> {
        let res;
        if (block_id) {
            res = await this.http.delete(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/blocks/${block_id}/vegetative_cycles/${cycle_number}`)
        } else {
            res = await this.http.delete(`/sitiagri-rest-api/api_basic/v1/plots/plots/${plot_id}/vegetative_cycles/${cycle_number}`)
        }
        propsToDate(res.data, ['start_date', 'end_date'])
        return res.data
    }


    async getAllColtivationSystems(filter: string | null): Promise<ColtivationSystem[]> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/dictionary_forcing_types`, {
            params: {
                filter: filter ? `ds_forcing like ${filter.toUpperCase()}` : null,
                sort: "+ds_forcing",
                offset: 0,
                count: 300
            }
        })

        return res.data.records;
    }

    public async checkSeedAuth(pkCuaa: number): Promise<boolean> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/user_registry/module_competencies/BEST_PRACTICE/SEED_EDIT?subject_id=${pkCuaa}`);
        return res.data.result;
    }

    public async getWarehouseSeed(pkCuaa: number, seedId: number): Promise<WarehouseSeed> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/warehouse/subjects/${pkCuaa}/seeds/${seedId}`);
        return res.data;
    }

}
